if (!customElements.get('testing-results-explorer')) {
  class TestingResultsExplorer extends HTMLElement {
    connectedCallback() {
      if (this.abortController) return;

      const store = window.EuroguardTestingStore;
      if (!store) return;

      this.store = store;
      const categories = [];
      const rows = [];
      this.querySelectorAll('[data-testing-category]').forEach((node) => {
        const id = node.dataset.categoryId;
        categories.push({ id, name: node.dataset.name, desc: node.dataset.description });
        try {
          const parsed = JSON.parse(JSON.parse(node.querySelector('[data-category-results]').textContent));
          if (!Array.isArray(parsed)) throw new Error('Expected a results array');
          parsed.forEach((row) => { if (row && row.id && row.n && row.res) rows.push({ ...row, c: id }); });
        } catch (error) {
          console.warn('Invalid testing category JSON:', id, error);
        }
      });
      let proto = {};
      try { proto = JSON.parse(JSON.parse(this.querySelector('[data-testing-protocols]').textContent)); }
      catch (error) { console.warn('Invalid testing protocols JSON', error); }
      this.data = { categories, rows, proto, carts: store.data.carts };
      this.state = { category: 'all', status: 'all', term: '' };
      this.abortController = new AbortController();
      const { signal } = this.abortController;

      this.search = this.querySelector('[data-search]');
      this.cartridgeSelect = this.querySelector('[data-cartridge-select]');
      this.statusSelect = this.querySelector('[data-status-select]');
      this.count = this.querySelector('[data-count]');
      this.chips = this.querySelector('[data-chips]');
      this.groups = this.querySelector('[data-groups]');
      this.empty = this.querySelector('[data-empty]');
      this.expandButton = this.querySelector('[data-expand]');
      this.labels = {
        all: this.dataset.allLabel,
        cert: this.dataset.certifiedLabel,
        lab: this.dataset.labLabel,
        pending: this.dataset.pendingLabel,
        nc: this.dataset.notClaimedLabel,
        expand: this.dataset.expandLabel,
        collapse: this.dataset.collapseLabel,
        singular: this.dataset.singularLabel,
        plural: this.dataset.pluralLabel,
        showing: this.dataset.showingLabel,
        of: this.dataset.ofLabel,
        for: this.dataset.forLabel,
        contaminantHeading: this.dataset.contaminantHeading,
        protocolHeading: this.dataset.protocolHeading,
        challengeHeading: this.dataset.challengeHeading,
        filteredHeading: this.dataset.filteredHeading,
        reductionHeading: this.dataset.reductionHeading,
        healthHeading: this.dataset.healthHeading,
      };

      this.buildControls();
      this.buildGroups();
      this.renderCartridge(store.cartridge);

      this.addEventListener('click', this.handleClick, { signal });
      this.search?.addEventListener('input', this.handleSearch, { signal });
      this.cartridgeSelect?.addEventListener('change', this.handleCartridgeSelect, { signal });
      this.statusSelect?.addEventListener('change', this.handleStatusSelect, { signal });
      window.addEventListener('euroguard:testing-cartridge-change', this.handleCartridgeChange, { signal });
      window.addEventListener('beforeprint', this.handleBeforePrint, { signal });
    }

    disconnectedCallback() {
      this.abortController?.abort();
      this.abortController = null;
    }

    escape(value) {
      return String(value ?? '').replace(/[&<>"']/g, (character) => ({
        '&': '&amp;',
        '<': '&lt;',
        '>': '&gt;',
        '"': '&quot;',
        "'": '&#039;',
      })[character]);
    }

    tag(status, label = null) {
      status = ['cert', 'lab', 'pending', 'nc'].includes(status) ? status : 'pending';
      return `<span class="eg-testing__tag eg-testing__tag--${status}">${this.escape(label || this.labels[status])}</span>`;
    }

    buildControls() {
      this.cartridgeSelect.innerHTML = this.data.carts
        .map((item) => `<option value="${this.escape(item.id)}">${this.escape(item.name + (item.tm || ''))}</option>`)
        .join('');

      this.chips.innerHTML = [
        `<button class="testing-results-explorer__chip" type="button" data-category="all" aria-pressed="true">${this.escape(this.labels.all)}</button>`,
        ...this.data.categories.map((category) =>
          `<button class="testing-results-explorer__chip" type="button" data-category="${this.escape(category.id)}" aria-pressed="false">${this.escape(category.name)}</button>`
        ),
      ].join('');
    }

    buildGroups() {
      this.groups.innerHTML = this.data.categories.map((category, index) => {
        const rows = this.data.rows.filter((row) => row.c === category.id);
        const body = rows.map((row) => `
          <tr data-row="${this.escape(row.id)}" data-search-value="${this.escape(`${row.n} ${row.a} ${category.name}`.toLowerCase())}">
            <td class="testing-results-explorer__name">${this.escape(row.n)}${row.a ? `<small>${this.escape(row.a)}</small>` : ''}</td>
            <td class="testing-results-explorer__protocol">${this.escape(this.data.proto[row.p] || row.p)}</td>
            <td class="testing-results-explorer__numeric" data-input></td>
            <td class="testing-results-explorer__numeric" data-output></td>
            <td class="testing-results-explorer__reduction" data-reduction></td>
            <td class="testing-results-explorer__health">${this.escape(row.h)}</td>
          </tr>
        `).join('');

        return `
          <details class="testing-results-explorer__group" data-group="${this.escape(category.id)}" ${index === 0 ? 'open' : ''}>
            <summary class="testing-results-explorer__summary">
              <h3>${this.escape(category.name)}</h3>
              <span class="testing-results-explorer__description">${this.escape(category.desc)}</span>
              <span class="testing-results-explorer__number"><span data-visible-count></span><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" aria-hidden="true"><path d="M6 9l6 6 6-6" stroke-linecap="round" stroke-linejoin="round"/></svg></span>
            </summary>
            <div class="testing-results-explorer__table-wrap">
              <table class="testing-results-explorer__table">
                <thead><tr><th>${this.escape(this.labels.contaminantHeading)}</th><th class="testing-results-explorer__protocol">${this.escape(this.labels.protocolHeading)}</th><th class="testing-results-explorer__numeric">${this.escape(this.labels.challengeHeading)}</th><th class="testing-results-explorer__numeric">${this.escape(this.labels.filteredHeading)}</th><th>${this.escape(this.labels.reductionHeading)}</th><th class="testing-results-explorer__health">${this.escape(this.labels.healthHeading)}</th></tr></thead>
                <tbody>${body}</tbody>
              </table>
            </div>
          </details>
        `;
      }).join('');

      this.rowIndex = new Map(this.data.rows.map((row) => [row.id, row]));
    }

    numeric(value, unit) {
      if (value == null) return `${this.tag('pending', '—')}`;
      return `${this.escape(value)} <small>${this.escape(unit)}</small>`;
    }

    reduction(result) {
      if (result.s === 'nc') return this.tag('nc');
      if (result.r == null) return this.tag('pending');

      const value = String(result.r).endsWith('%') ? result.r : `${result.r}%`;
      return `<strong>${this.escape(value)}</strong><br>${this.tag(result.s)}`;
    }

    renderCartridge(cartridgeId) {
      const cartridge = this.store.getCartridge(cartridgeId);
      this.activeCartridge = cartridge;
      this.cartridgeSelect.value = cartridge.id;

      this.groups.querySelectorAll('[data-row]').forEach((tableRow) => {
        const row = this.rowIndex.get(tableRow.dataset.row);
        const result = row?.res?.[cartridge.id] || { s: 'nc', in: null, out: null, r: null };
        if (!row || !result) return;

        tableRow.dataset.status = result.s;
        tableRow.querySelector('[data-input]').innerHTML = result.s === 'nc' ? '' : this.numeric(result.in, row.u);
        tableRow.querySelector('[data-output]').innerHTML = result.s === 'nc' ? '' : this.numeric(result.out, row.u);
        tableRow.querySelector('[data-reduction]').innerHTML = this.reduction(result);
      });

      this.applyFilters();
      this.removeAttribute('aria-busy');
    }

    applyFilters() {
      let totalVisible = 0;

      this.groups.querySelectorAll('[data-group]').forEach((group) => {
        const categoryMatches = this.state.category === 'all' || group.dataset.group === this.state.category;
        let visibleInGroup = 0;

        group.querySelectorAll('[data-row]').forEach((row) => {
          const matches = categoryMatches
            && (this.state.status === 'all' || row.dataset.status === this.state.status)
            && (!this.state.term || row.dataset.searchValue.includes(this.state.term));

          row.hidden = !matches;
          if (matches) visibleInGroup += 1;
        });

        group.hidden = visibleInGroup === 0;
        const label = visibleInGroup === 1 ? this.labels.singular : this.labels.plural;
        group.querySelector('[data-visible-count]').textContent = `${visibleInGroup} ${label}`;
        if (this.state.term && visibleInGroup) group.open = true;
        totalVisible += visibleInGroup;
      });

      this.count.textContent = `${this.labels.showing} ${totalVisible} ${this.labels.of} ${this.data.rows.length} ${this.labels.for} ${this.activeCartridge.name}${this.activeCartridge.tm || ''}`;
      this.empty.hidden = totalVisible !== 0;
    }

    handleClick = (event) => {
      const categoryButton = event.target.closest('[data-category]');
      if (categoryButton && this.contains(categoryButton)) {
        this.state.category = categoryButton.dataset.category;
        this.chips.querySelectorAll('[data-category]').forEach((button) => {
          button.setAttribute('aria-pressed', String(button === categoryButton));
        });
        const group = this.groups.querySelector(`[data-group="${CSS.escape(this.state.category)}"]`);
        if (group) group.open = true;
        this.applyFilters();
        return;
      }

      if (event.target.closest('[data-expand]')) {
        const shouldOpen = this.expandButton.getAttribute('aria-pressed') !== 'true';
        this.groups.querySelectorAll('[data-group]').forEach((group) => { group.open = shouldOpen; });
        this.expandButton.setAttribute('aria-pressed', String(shouldOpen));
        this.expandButton.textContent = shouldOpen ? this.labels.collapse : this.labels.expand;
        return;
      }

      if (event.target.closest('[data-print]')) window.print();
    };

    handleSearch = (event) => {
      this.state.term = event.target.value.trim().toLowerCase();
      this.applyFilters();
    };

    handleCartridgeSelect = (event) => {
      const id = event.target.value;
      this.store.selectCartridge(id);
      this.renderCartridge(id);
    };

    handleStatusSelect = (event) => {
      this.state.status = event.target.value;
      this.applyFilters();
    };

    handleCartridgeChange = (event) => {
      this.data.carts = this.store.data.carts;
      this.buildControls();
      this.chips.querySelectorAll('[data-category]').forEach((button) => button.setAttribute('aria-pressed', String(button.dataset.category === this.state.category)));
      this.renderCartridge(event.detail?.cartridge);
    };

    handleBeforePrint = () => {
      this.groups.querySelectorAll('[data-group]').forEach((group) => { group.open = true; });
    };
  }

  customElements.define('testing-results-explorer', TestingResultsExplorer);
}
